import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import type { MetricRecord } from "@/metrics/runtime"
import { toast } from "@coval/ui"

import { logger } from "@/lib/logger"
import type { MetricOutputDisplaySource } from "@/types/RunMetricOutput"

import type { CriterionResult } from "../types"
import {
  useMetricOutputsByUlidsTransport,
  useRunTestMetricTransport,
} from "./routes/useTestMetricTransport"
import { resolveRunSelection } from "./runSelection"
import type {
  ExperimentTestResult as ExperimentResult,
  TestMetricResult,
} from "./types"
import { extractCompositeResults } from "./utils/compositeResults"

const log = logger("useBackendExperimentRunner")

const POLL_INTERVAL_MS = 1500
const POLL_TIMEOUT_MS = 5 * 60 * 1000
const TERMINAL_STATUSES = new Set(["COMPLETED", "FAILED", "SKIPPED"])
const TIMEOUT_ERROR_MESSAGE = "Test metric evaluation timed out after 5 minutes"
const LAUNCH_FAILED_MESSAGE = "Failed to start test metric evaluation"
const PREPARE_FAILED_MESSAGE = "Failed to save the current test criteria"

// Cap how many invocations hit the endpoint at once so a large sim set doesn't
// flood it and trip its rate limit (429).
const INVOKE_BATCH_SIZE = 10
const INVOKE_BATCH_DELAY_MS = 100

export const PASSING_VALUES = new Set(["Yes", "Pass", "TRUE"])

interface Progress {
  current: number
  total: number
}

interface UseBackendExperimentRunnerOptions {
  metric: MetricRecord | null
  onResult: (simId: string, result: ExperimentResult) => void
  onComplete: () => void
  simulationIds?: string[]
  devId?: string
  beforeRun?: () => Promise<void>
  criteriaCount?: number
}

function formatCompositeValue(
  floatValue: number,
  reportingMethod: string,
  criteriaCount: number,
): string {
  if (reportingMethod === "all_criteria_met") {
    return floatValue === 1 ? "Pass" : "Fail"
  }
  if (reportingMethod === "count_of_criteria_met" && criteriaCount > 0) {
    const metCount = Math.round(floatValue * criteriaCount)
    return String(metCount)
  }
  return `${Math.round(floatValue * 100)}%`
}

function formatResultValue(
  result: TestMetricResult,
  metric: MetricRecord | null,
  criteriaCountOverride?: number,
): string | null {
  const isComposite = metric?.metric_manager === "manager_composite_evaluation"

  if (isComposite && result.float_value != null) {
    const reportingMethodValue = metric.metric_metadata?.reporting_method
    const reportingMethod =
      typeof reportingMethodValue === "string"
        ? reportingMethodValue
        : "percentage_of_criteria_met"
    const criteria = metric.metric_metadata?.criteria
    const criteriaCount =
      criteriaCountOverride ?? (Array.isArray(criteria) ? criteria.length : 0)
    return formatCompositeValue(
      result.float_value,
      reportingMethod,
      criteriaCount,
    )
  }

  if (result.float_value != null) {
    return String(result.float_value)
  }
  if (result.string_value != null) {
    return result.string_value
  }
  if (result.set_value != null) {
    return result.set_value.join(", ")
  }
  return null
}

function getLlmExplanation(
  obj: Record<string, unknown> | null,
): string | undefined {
  if (!obj || typeof obj !== "object") {
    return undefined
  }
  const llm = obj.llm
  if (!llm || typeof llm !== "object") {
    return undefined
  }
  const explanation = (llm as Record<string, unknown>).answer_explanation
  return typeof explanation === "string" ? explanation : undefined
}

function extractExplanation(result: TestMetricResult): string {
  return (
    getLlmExplanation(result.result) ??
    getLlmExplanation(result.result_json) ??
    ""
  )
}

function toDisplaySource(result: TestMetricResult): MetricOutputDisplaySource {
  return {
    output_type: result.output_type,
    float_value: result.float_value,
    string_value: result.string_value,
    set_value: result.set_value,
    result: result.result,
    result_json: result.result_json,
    evidence_references: result.evidence_references,
    subvalues_by_timestamp: result.subvalues_by_timestamp as
      | MetricOutputDisplaySource["subvalues_by_timestamp"]
      | null,
  }
}

function extractCriteriaResults(
  result: TestMetricResult,
): CriterionResult[] | undefined {
  const criteriaResults = extractCompositeResults(result)
  return criteriaResults.length > 0 ? criteriaResults : undefined
}

export const useBackendExperimentRunner = ({
  metric,
  onResult,
  onComplete,
  simulationIds,
  devId,
  beforeRun,
  criteriaCount,
}: UseBackendExperimentRunnerOptions) => {
  const [metricOutputUlids, setMetricOutputUlids] = useState<string[]>([])
  const [activeSimIds, setActiveSimIds] = useState<string[]>([])
  const [isPreparing, setIsPreparing] = useState(false)
  const [isRunning, setIsRunning] = useState(false)
  const [isStopping, setIsStopping] = useState(false)
  const [completedIds, setCompletedIds] = useState<Set<string>>(new Set())
  const [pollStartedAt, setPollStartedAt] = useState<number | null>(null)
  // Monotonic token identifying the active run. Each invocation claims a new
  // token; a stop or a newer run supersedes it, so a paused older invocation
  // bails between batches instead of launching or publishing over the new run.
  const runTokenRef = useRef(0)

  // Invalidate a save that resolves after its drawer (or source tab) has
  // unmounted. Without this, the detached continuation can still launch the
  // evaluation for the row the user already left.
  useEffect(
    () => () => {
      runTokenRef.current++
    },
    [],
  )

  const runTestMetricMutation = useRunTestMetricTransport()

  // Sims we've launched that haven't reached a terminal status yet.
  const evaluatingIds = useMemo(() => {
    const next = new Set<string>()
    for (const id of activeSimIds) {
      if (!completedIds.has(id)) {
        next.add(id)
      }
    }
    return next
  }, [activeSimIds, completedIds])

  const progress: Progress = useMemo(
    () => ({ current: completedIds.size, total: activeSimIds.length }),
    [completedIds, activeSimIds],
  )

  const resetRunState = useCallback(() => {
    setMetricOutputUlids([])
    setActiveSimIds([])
    setCompletedIds(new Set())
    setPollStartedAt(null)
  }, [])

  const prepareRun = useCallback(async () => {
    if (!beforeRun) {
      return true
    }
    try {
      await beforeRun()
      return true
    } catch (error) {
      log.error(PREPARE_FAILED_MESSAGE, error)
      toast.danger({
        title: PREPARE_FAILED_MESSAGE,
        description: error instanceof Error ? error.message : "Unknown error",
      })
      return false
    }
  }, [beforeRun])

  // 1. Invoke the test-metric Lambda once per (metric, simulation) pair. The
  // Lambda accepts a single simulation_output_id per invocation to avoid
  // breaking the p90 latency target on large batches. Invocations run in
  // bounded-concurrency batches so a large sim set doesn't flood the endpoint.
  const invokeLambdas = useCallback(
    async (metricId: string, simIds: string[]) => {
      const runToken = ++runTokenRef.current
      setIsRunning(true)
      setIsStopping(false)
      setActiveSimIds(simIds)
      setCompletedIds(new Set())
      // Timeout is stamped only once launching finishes (below) so a long
      // batched launch doesn't eat into the polling window.
      setPollStartedAt(null)
      setMetricOutputUlids([])

      const ulids: string[] = []
      const failedSimIds: string[] = []
      for (let start = 0; start < simIds.length; start += INVOKE_BATCH_SIZE) {
        if (runTokenRef.current !== runToken) {
          return
        }
        const batch = simIds.slice(start, start + INVOKE_BATCH_SIZE)
        const settled = await Promise.allSettled(
          batch.map((simId) =>
            runTestMetricMutation.mutateAsync({
              metric_id: metricId,
              simulation_output_id: simId,
              dev_id: devId,
            }),
          ),
        )
        for (let i = 0; i < settled.length; i++) {
          const result = settled[i]
          if (result.status === "fulfilled") {
            ulids.push(result.value.metric_output_ulid)
          } else {
            failedSimIds.push(batch[i])
            log.error("Failed to invoke test-metric lambda", result.reason)
          }
        }
        if (start + INVOKE_BATCH_SIZE < simIds.length) {
          await new Promise((resolve) => {
            setTimeout(resolve, INVOKE_BATCH_DELAY_MS)
          })
        }
      }

      // A stop or a newer run superseded this one; don't publish results or
      // re-enable polling for an aborted/stale run.
      if (runTokenRef.current !== runToken) {
        return
      }

      // Surface launch failures immediately so the row renders an error
      // badge instead of staying in the loading state — and so the timeout
      // guard doesn't later mislabel them as "timed out".
      for (const simId of failedSimIds) {
        onResult(simId, {
          value: null,
          explanation: "",
          errorStatus: "FAILED",
          errorMessage: LAUNCH_FAILED_MESSAGE,
        })
      }
      if (failedSimIds.length > 0) {
        setCompletedIds((prev) => {
          const next = new Set(prev)
          for (const simId of failedSimIds) {
            next.add(simId)
          }
          return next
        })
        toast.danger({
          title: `${failedSimIds.length} of ${simIds.length} test-metric invocations failed`,
        })
      }

      if (ulids.length === 0) {
        setIsRunning(false)
        resetRunState()
        onComplete()
        return
      }

      // Launch is done — start the poll timeout window now.
      setPollStartedAt(Date.now())
      setMetricOutputUlids(ulids)
    },
    [runTestMetricMutation, onComplete, onResult, resetRunState, devId],
  )

  const runExperiment = useCallback(async () => {
    if (!metric?.id || !simulationIds?.length) {
      return
    }
    const preparationToken = ++runTokenRef.current
    setIsPreparing(true)
    const isPrepared = await prepareRun()
    if (runTokenRef.current !== preparationToken) {
      return
    }
    setIsPreparing(false)
    if (!isPrepared) {
      return
    }
    const runIds = resolveRunSelection(simulationIds, null).runIds
    await invokeLambdas(metric.id, runIds)
  }, [metric, simulationIds, prepareRun, invokeLambdas])

  const runSingleExperiment = useCallback(
    async (simId: string) => {
      if (!metric?.id) {
        return
      }
      const preparationToken = ++runTokenRef.current
      setIsPreparing(true)
      const isPrepared = await prepareRun()
      if (runTokenRef.current !== preparationToken) {
        return
      }
      setIsPreparing(false)
      if (!isPrepared) {
        return
      }
      await invokeLambdas(metric.id, [simId])
    },
    [metric, prepareRun, invokeLambdas],
  )

  const stopExperiment = useCallback(() => {
    runTokenRef.current++
    setIsPreparing(false)
    setIsStopping(true)
    setIsRunning(false)
    resetRunState()
    setIsStopping(false)
    onComplete()
  }, [resetRunState, onComplete])

  // 2. Poll by ULID list every 1.5s until all results terminal or timeout.
  const {
    data: results,
    isError: isPollError,
    error: pollError,
  } = useMetricOutputsByUlidsTransport(metricOutputUlids, {
    enabled: isRunning,
    refetchInterval: POLL_INTERVAL_MS,
  })

  // 3. Emit granular onResult callbacks as results arrive.
  useEffect(() => {
    if (!results) {
      return
    }

    for (const result of results) {
      const status = result.error_status ?? ""
      const isTerminal = TERMINAL_STATUSES.has(status)

      if (isTerminal && !completedIds.has(result.simulation_output_id)) {
        setCompletedIds((prev) =>
          new Set(prev).add(result.simulation_output_id),
        )
        onResult(result.simulation_output_id, {
          value: formatResultValue(result, metric, criteriaCount),
          rawFloatValue: result.float_value,
          criteriaResults: extractCriteriaResults(result),
          explanation: extractExplanation(result),
          errorStatus: result.error_status ?? undefined,
          errorMessage: result.error_message ?? undefined,
          rawMetricOutput: toDisplaySource(result),
        })
      }
    }

    const allDone =
      results.length > 0 &&
      results.every((r) => TERMINAL_STATUSES.has(r.error_status ?? ""))

    if (allDone) {
      setIsRunning(false)
      onComplete()
    }
  }, [results, completedIds, onResult, onComplete, metric, criteriaCount])

  // 4. Polling-error guard.
  useEffect(() => {
    if (!isRunning || !isPollError) {
      return
    }
    log.error("Polling failed", pollError)
    setIsRunning(false)
    toast.danger({
      title: "Failed to poll test-metric results",
      description:
        pollError instanceof Error ? pollError.message : "Unknown error",
    })
    onComplete()
  }, [isRunning, isPollError, pollError, onComplete])

  // 5. Timeout guard — uses an interval so it fires even when deps are stable.
  useEffect(() => {
    if (!isRunning || !pollStartedAt) {
      return
    }

    const id = setInterval(() => {
      if (Date.now() - pollStartedAt > POLL_TIMEOUT_MS) {
        setIsRunning(false)
        toast.danger({
          title: "Test-metric polling timed out after 5 minutes",
        })

        // Emit a FAILED result for every sim still in-flight so the table
        // renders the error badge instead of an indefinite loading bar.
        const pendingSimIds = activeSimIds.filter(
          (simId) => !completedIds.has(simId),
        )
        for (const simId of pendingSimIds) {
          onResult(simId, {
            value: null,
            explanation: "",
            errorStatus: "FAILED",
            errorMessage: TIMEOUT_ERROR_MESSAGE,
          })
        }
        if (pendingSimIds.length > 0) {
          setCompletedIds((prev) => {
            const next = new Set(prev)
            for (const simId of pendingSimIds) {
              next.add(simId)
            }
            return next
          })
        }

        onComplete()
      }
    }, POLL_INTERVAL_MS)

    return () => clearInterval(id)
  }, [
    isRunning,
    pollStartedAt,
    onComplete,
    onResult,
    activeSimIds,
    completedIds,
  ])

  return {
    runExperiment,
    runSingleExperiment,
    stopExperiment,
    // Preparing is part of the run lifecycle: callers use this state to
    // disable navigation while current criteria are being persisted.
    isRunning: isPreparing || isRunning,
    isStopping,
    progress,
    evaluatingIds,
  }
}
